import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { useSpring } from "@react-spring/three";
import { invalidate, useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { Quaternion } from "three";
import { useShallow } from "zustand/shallow";

// modify this to change the animation speed
const animationInterval = 1000 / 15;

export const useAnimatedFrameData = (
  id: string,
  isDieNodeReady: boolean,
  onAnimationStarted?: () => void,
  onAnimationComplete?: () => void,
  preview?: boolean,
) => {
  const frameData = useRenderedDiceStore(
    useShallow((state) =>
      state.renderedDice.entities[id]?.frameData.filter((f) => f && f.rotation),
    ),
  );
  const [currentAnimationFrame, setCurrentAnimationFrame] = useState(0);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const currentFrameRef = useRef(0);
  const previousQuaternion = useRef<Quaternion>(new Quaternion());
  const nextQuaternion = useRef<Quaternion>(new Quaternion());
  // frameProgress is a value between 0 and 1 that resets every animationInterval
  // which we use to interpolate between the previous and next quaternions
  const [{ position, frameProgress }, api] = useSpring(() => ({
    from: {
      position: frameData[0]?.position ?? [0, 0, 0],
      frameProgress: 0,
    },
    config: { duration: preview ? 0 : animationInterval },
    onChange: preview ? undefined : () => setTimeout(() => invalidate(), 1),
  }));
  // Use the frameProgress to interpolate between previous and next quaternions
  // disabled because we are using refs to store the quaternions on purpose
  // eslint-disable-next-line react-hooks/refs
  const quaternion = frameProgress.to((progress) =>
    previousQuaternion.current.clone().slerp(nextQuaternion.current, progress),
  );
  const hasAnimationStarted = useRef(false);
  const lastFrameTimeMs = useRef(0);

  useEffect(() => {
    if (!frameData[currentAnimationFrame] || preview) return;

    invalidate();
    // set the new position and begin another frameProgress timer to slerp the quaternions
    api.start({
      from: { frameProgress: 0 },
      to: {
        position: frameData[currentAnimationFrame].position,
        frameProgress: 1,
      },
    });

    // update previous and next quaternions based on the current frame
    previousQuaternion.current.set(
      ...frameData[Math.max(0, currentAnimationFrame - 1)].rotation!,
    );
    nextQuaternion.current.set(...frameData[currentAnimationFrame].rotation!);
  }, [currentAnimationFrame, api, frameData, preview]);

  useFrame((state) => {
    if (preview || !isDieNodeReady || isAnimationComplete) return;

    currentFrameRef.current += 1;

    const elapsedMs = state.clock.getElapsedTime() * 1000;

    if (elapsedMs - lastFrameTimeMs.current >= animationInterval) {
      lastFrameTimeMs.current = elapsedMs;

      if (!hasAnimationStarted.current) {
        onAnimationStarted?.();

        hasAnimationStarted.current = true;
      }

      if (currentAnimationFrame === frameData.length - 1) {
        onAnimationComplete?.();
        setIsAnimationComplete(true);
      } else {
        setCurrentAnimationFrame((prev) => prev + 1);
      }
    }

    invalidate();
  });

  return { position, quaternion, currentFrameRef };
};
